import React, { useState } from "react";
import { Box, Chip, alpha } from "@mui/material";

const toggleChipSx = {
  fontWeight: 400,
  color: "secondary.main",
  borderColor: "secondary.main",
  bgcolor: "background.paper",
  "&&:hover": {
    bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
  },
};

const plusSx = { fontSize: "1.25em", lineHeight: 1, verticalAlign: "-0.05em" };

/**
 * Autocomplete tags that collapse after `visibleCount` into a "+N" chip, with "Show Less" to collapse again.
 * Use in `renderTags={(value, getTagProps) => <ExpandableTags value={value} getTagProps={getTagProps} />}`.
 *
 * @param {Array} value - Selected options.
 * @param {Function} getTagProps - The Autocomplete's getTagProps.
 * @param {number} [visibleCount=6] - Chips shown before "+N".
 * @param {Function} [getLabel] - Label for an option (default: the option).
 * @param {Object} [chipProps] - Extra props for each chip.
 * @param {string} [lessLabel="Show Less"] - Toggle label when expanded.
 * @param {string} [itemLabel] - Item name for screen readers, e.g. "recipients".
 */
const ExpandableTags = ({
  value = [],
  getTagProps,
  visibleCount = 6,
  getLabel = (option) => option,
  chipProps = {},
  lessLabel = "Show Less",
  itemLabel = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hiddenCount = value.length - visibleCount;
  const visible = isExpanded ? value : value.slice(0, visibleCount);

  const isDisabled =
    hiddenCount > 0 && Boolean(getTagProps({ index: 0 }).disabled);

  return (
    <>
      {visible.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={getLabel(option)}
          label={getLabel(option)}
          size="small"
          {...chipProps}
        />
      ))}

      {hiddenCount > 0 && (
        <Chip
          label={
            isExpanded ? (
              lessLabel
            ) : (
              <>
                <Box component="span" sx={plusSx}>
                  +
                </Box>
                {hiddenCount}
              </>
            )
          }
          size="small"
          variant="outlined"
          disabled={isDisabled}
          sx={toggleChipSx}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? `Show fewer ${itemLabel}`.trim()
              : `Show ${hiddenCount} more ${itemLabel}`.trim()
          }
          // Keep the Autocomplete list closed
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setIsExpanded((prev) => !prev)}
        />
      )}
    </>
  );
};

export default ExpandableTags;
